import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, Body, Card, Button } from '../ui';
import StatisticsCard from '../ui/StatisticsCard';
import { useAuth } from '../../contexts/AuthContext';
import { AdminDashboardStats } from '../../services/api/dashboard';
import { formatRelative } from '../../utils/date';

interface AdminDashboardProps {
  stats?: AdminDashboardStats;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ stats }) => {
  const { user } = useAuth();

  const quickActions = [
    {
      title: 'View All Children',
      description: 'Manage child records across all classrooms',
      icon: UserGroupIcon,
      href: '/children',
      color: 'primary' as const,
    },
    {
      title: 'Recent Observations',
      description: 'Review latest teacher observations',
      icon: ClipboardDocumentListIcon,
      href: '/observations',
      color: 'secondary' as const,
    },
    {
      title: 'Assessment Reports',
      description: 'View and generate assessment reports',
      icon: ChartBarIcon,
      href: '/assessments',
      color: 'success' as const,
    },
    {
      title: 'Manage Classrooms',
      description: 'Organize classes and teacher assignments',
      icon: BuildingOfficeIcon,
      href: '/classrooms',
      color: 'warning' as const,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <H1 className="mb-2">Welcome back, {user?.first_name}!</H1>
        <Body className="text-gray-600 dark:text-gray-400">
          Here's what's happening in your school today.
        </Body>
      </motion.div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatisticsCard
          title="Total Children"
          value={stats?.total_children ?? 0}
          icon={UserGroupIcon}
          color="primary"
        />
        <StatisticsCard
          title="Total Observations"
          value={stats?.total_observations ?? 0}
          icon={ClipboardDocumentListIcon}
          color="secondary"
        />
        <StatisticsCard
          title="Active Teachers"
          value={stats?.total_teachers ?? 0}
          icon={AcademicCapIcon}
          color="success"
        />
        <StatisticsCard
          title="This Week"
          value={stats?.observations_this_week ?? 0}
          icon={ChartBarIcon}
          color="warning"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <H2 className="mb-6">Quick Actions</H2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Link to={action.href}>
                <Card className="p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-full ${
                      action.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                      action.color === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
                      action.color === 'success' ? 'bg-green-100 text-green-600' :
                      action.color === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      <action.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <H2 className="text-lg mb-2">{action.title}</H2>
                  <Body className="text-sm text-gray-600 dark:text-gray-400">
                    {action.description}
                  </Body>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <H2 className="mb-6">Recent Activity</H2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="text-lg">Recent Observations</H2>
              <Link to="/observations">
                <Button variant="ghost" size="sm">
                  View all
                </Button>
              </Link>
            </div>
            <div className="space-y-1">
              {stats?.recent_observations && stats.recent_observations.length > 0 ? (
                stats.recent_observations.map((item) => (
                  <Link
                    key={item.id}
                    to={`/observations?id=${item.id}`}
                    className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <Body className="font-medium truncate">{item.child_name}</Body>
                      <Body className="text-sm text-gray-500">by {item.teacher_name}</Body>
                    </div>
                    <Body className="text-sm text-gray-500 whitespace-nowrap">
                      {formatRelative(item.observation_date)}
                    </Body>
                  </Link>
                ))
              ) : (
                <Body className="text-gray-400 text-center py-4">No observations yet</Body>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="text-lg">Recent Assessments</H2>
              <Link to="/assessments">
                <Button variant="ghost" size="sm">
                  View all
                </Button>
              </Link>
            </div>
            <div className="space-y-1">
              {stats?.recent_assessments && stats.recent_assessments.length > 0 ? (
                stats.recent_assessments.map((item) => (
                  <Link
                    key={item.id}
                    to={`/assessments?id=${item.id}`}
                    className="block py-2 px-2 -mx-2 rounded-md border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Body className="font-medium">{item.child_name}</Body>
                    <div className="flex items-center justify-between mt-1">
                      <Body className="text-sm text-gray-500">{item.learning_area_name}</Body>
                      <Body className="text-sm text-gray-500">
                        {formatRelative(item.assessment_date)}
                      </Body>
                    </div>
                  </Link>
                ))
              ) : (
                <Body className="text-gray-400 text-center py-4">No assessments yet</Body>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
